<template>
  <div class="term">
    <div class="term__main">
      <a-term-details
        :title="term.Name"
        :description="term.Description"
        :term-id="term.Id"
        :term-name="term.Name"
      />

      <a-banner
        class="term__banner"
        :banner-settings="
          $options.consts.BANNER_SETTINGS.TERM_PAGE.MIDDLE_BANNER
        "
      />

      <a-related-articles
        :pagination="pagination"
        :pages-count="pagesCount"
        :term-id="term.Id"
        :term-name="term.Name"
        :related-terms="term.RelatedTerms"
        :directory-categories="directoryCategories"
        :all-articles="relatedArticles"
        :in-progress="inProgress"
        @get-new-chunk="getNextArticleChunk"
      />
      <a-vue-disqus
        v-if="isNoRelatedArticles"
        :title="term.Name"
        :identifier="term.Id"
        :url="canonicalUrlWithoutPagination"
        :category_id="$env.FM_DISQUS_TERM_ID"
      />
    </div>

    <a-visibility hide :to="$breakpoint.desktopMd">
      <a-related-terms
        :related-terms="term.RelatedTerms"
        :directory-categories="directoryCategories"
      />
    </a-visibility>

    <a-sticky-banner
      :sticky-position="$options.consts.STICKY_BANNER_POSITION.BOTTOM"
      :banner-settings="
        $options.consts.BANNER_SETTINGS.TERM_PAGE.STICKY_TO_BOTTOM
      "
    />
    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
    <a-url-pagination
      :item-count="relatedArticles.length"
      :pagination.sync="pagination"
      :initial-page="initialPage"
      :pages-count="pagesCount"
      :item-selector="$options.consts.ARTICLE_SLOT_SELECTOR"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGOUT,
  METHOD_NAME_TO_RUN_ON_LOGIN
} from '@/utils/mixins/run-method-on-login-logout'
import ATermDetails from '@/components/_sections/terms/ATermDetails'
import ARelatedArticles from '@/components/_sections/terms/ARelatedArticles'
import ABanner from 'shared/ABanner'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import { ERROR_CODE } from 'enums/errorCodes'
import { PAGE_SCHEME_TYPE } from '@/utils/enums/pageSchemes'
import { throwPageNotFoundError } from '@/plugins/error'
import { getParsedHtmlWithReplacedMediaAttrs } from '@/utils/helpers/mediaProperties'
import AStickyBanner from 'shared/AStickyBanner'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import AVueDisqus from 'shared/AVueDisqus/lazy'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import { getPageNumberFromRoute } from '@/plugins/helper'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { applyFallback } from '@/utils/helpers'
import { ARTICLE_SLOT_CLASS_NAME } from 'shared/APaginatedNewsList/enums'
import AUrlPagination from 'shared/AUrlPagination/index.vue'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import { ACTION_ICON } from 'shared/ANewsItem'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { generateFmUrl } from '@/utils/helpers/url-generators'
import { handleNextChunks } from '@/utils/helpers/handle-duplicates'

const TERM_MODEL = {
  Id: '',
  Name: '',
  Description: '',
  DescriptionImages: [],
  DescriptionVideos: [],
  RelatedTerms: [
    {
      Name: '',
      Slug: ''
    }
  ]
}

const TAKE_PER_REQUEST = 25

function checkTermRouteParams({ slug, letter }) {
  return slug.charAt(0).toLowerCase() === letter.toLowerCase()
}

function throwError(ctx) {
  const err = {
    response: {
      status: 404,
      data: { ErrorCode: ERROR_CODE.TERM_NOT_FOUND }
    }
  }
  ctx.app.$error(err)
}

export default {
  name: 'TermSlug',
  scrollToTop: true,
  mixins: [
    mixins.refreshDataHandler,
    mixins.runMethodOnLoginLogout,
    mixins.setPageLoaded,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE
      }
    })
  ],
  components: {
    AStickyBanner,
    ABanner,
    ATermDetails,
    ARelatedTerms: hydrateWhenVisible(
      () => import('@/components/_sections/terms/ARelatedTerms'),
      { props: ['related-terms', 'directory-categories'] }
    ),
    ARelatedArticles,
    AVueDisqus,
    APaginationLinksForGoogle,
    AUrlPagination
  },
  head() {
    const { Name } = this.term

    const initialPage = getPageNumberFromRoute(this.$route)

    return this.$generateMetaTags({
      titleParam: Name,
      descriptionParam: Name,
      schemes: [
        { type: PAGE_SCHEME_TYPE.ORGANIZATION },
        { type: PAGE_SCHEME_TYPE.WEB_SITE },
        ...this.$helper.generateVideoSchemasFromResponse(
          this.term.DescriptionVideos
        )
      ],
      additionalLinks: this.$helper.getPaginationSeoLinks({
        canonicalUrlWithoutPagination: this.canonicalUrlWithoutPagination,
        pagesCount: this.pagesCount,
        page: initialPage + 1
      }),
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route
    const { slug: termSlug, letter } = ctx.route.params
    const initialPage = getPageNumberFromRoute(ctx.route)

    try {
      if (!checkTermRouteParams(ctx.route.params)) return throwError(ctx)

      const pagination = {
        take: TAKE_PER_REQUEST,
        page: initialPage
      }
      const dispatch = ctx.store.dispatch

      const [
        { Items: directoryCategories },
        term,
        { Articles, PagesCount: pagesCount }
      ] = await Promise.all([
        dispatch('directory/requestDirectoryCategoriesRotated'),
        dispatch('terms/requestTermBySlug', {
          pathParams: {
            slug: termSlug
          }
        }),
        dispatch('terms/requestRelatedArticles', {
          termSlug,
          ...pagination
        })
      ])

      if (pagesCount && initialPage > pagesCount) {
        return throwPageNotFoundError(ctx, 'TermPage')
      }

      const canonicalUrlWithoutPagination = generateFmUrl(
        `/terms/${letter.toLowerCase()}/${term.Slug}/`,
        ctx
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: currentPath
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        canonicalUrl,
        canonicalUrlWithoutPagination,
        directoryCategories,
        term: applyFallback(
          {
            ...term,
            Description: getParsedHtmlWithReplacedMediaAttrs(term.Description, {
              images: term.DescriptionImages,
              videos: term.DescriptionVideos
            })
          },
          TERM_MODEL
        ),
        relatedArticles: Articles,
        initialPage,
        pagesCount,
        pagination
      }
    } catch (err) {
      throwPageNotFoundError(ctx, 'TermPage')
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  consts: {
    BANNER_SETTINGS,
    STICKY_BANNER_POSITION,
    HTML_TAG,
    ARTICLE_SLOT_SELECTOR: `.${ARTICLE_SLOT_CLASS_NAME}`,
    RELATED_ARTICLES_ACTION_ICONS: [ACTION_ICON.SAVE, ACTION_ICON.SHARE]
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      directoryCategories: [],
      term: {},
      relatedArticles: [],
      initialPage,
      pagesCount: 0,
      pagination: {
        take: TAKE_PER_REQUEST,
        page: initialPage
      },
      shift: 0,
      inProgress: false
    }
  },
  computed: {
    isNoRelatedArticles() {
      return this.relatedArticles.length === 0
    }
  },
  methods: {
    ...mapActions({
      requestRelatedArticles: 'terms/requestRelatedArticles'
    }),
    async getRelatedArticles(pagination) {
      try {
        const payload = {
          termSlug: this.$route.params.slug,
          ...pagination
        }
        const { Articles, PagesCount } = await this.requestRelatedArticles(
          payload
        )
        this.pagesCount = PagesCount

        return Articles
      } catch (err) {
        this.$errorHandler(err, this)
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      handleNextChunks({
        shift: this.shift,
        pagination: this.pagination,
        pagesCount: this.pagesCount,
        setInProgressTrueFn: () => {
          this.inProgress = true
        },
        setInProgressFalseFn: () => {
          this.inProgress = false
        },
        setShiftFn: newVal => {
          this.shift = newVal
        },
        requestNextChunkFn: this.getRelatedArticles.bind(this),
        addNewArticlesFn: async newArticles => {
          this.relatedArticles = [...this.relatedArticles, ...newArticles]
          await this.$nextTick()
        },
        getExistingArticleIdsFn: () => this.relatedArticles.map(({ id }) => id)
      })
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE](payload) {
      this.relatedArticles = this.$_refreshDataHandler_updateIsSavedValue(
        payload,
        this.relatedArticles
      )
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE](payload) {
      this.relatedArticles = this.$_refreshDataHandler_updateIsFollowedTagValue(
        {
          ...payload,
          articles: this.relatedArticles
        }
      )
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.relatedArticles = this.$_refreshDataHandler_resetActionIconValues(
        this.relatedArticles
      )
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const {
        Article,
        Tag
      } = await this.$_refreshDataHandler_getSavedIdsOfEntities([
        this.$_refreshDataHandler_savedEntity.ARTICLE,
        this.$_refreshDataHandler_savedEntity.TAG
      ])
      this.relatedArticles = this.$_refreshDataHandler_updateArticleSavedEntities(
        {
          articleIds: Article,
          tagIds: Tag
        },
        this.relatedArticles
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.term {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  &__main {
    width: percentage(1000px / $desktop-full-hd-container);
    margin-right: 20px;

    @include desktop-lg {
      width: 63%;
    }

    @include desktop-md {
      width: 100%;
      margin-right: 0;
    }
  }

  &__banner {
    margin-top: 40px;
    margin-bottom: 40px;

    @include mobile {
      margin-top: 30px;
      margin-bottom: 30px;
    }
  }
}
</style>
